import {
  COMPANY_LABELS,
  EQUIPMENT_CATEGORY_LABELS,
  daysUntil,
  formatDateLong,
  formatNumber,
} from '@nova/shared';
import { useQuery } from '@tanstack/react-query';
import { CalendarClock, Cpu, KeyRound, Laptop, Package } from 'lucide-react';
import { EmptyState } from '../../components/EmptyState.js';
import { GlassPanel, PanelHeader } from '../../components/GlassPanel.js';
import { SkeletonPanel } from '../../components/Skeleton.js';
import { api } from '../../lib/api.js';
import { cn } from '../../lib/cn.js';
import type { OverviewDto } from './types.js';

const kr = (n: number) => `${formatNumber(n)} kr`;

export function OverviewTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['assets', 'overview'],
    queryFn: () => api.get<OverviewDto>('/assets/admin/overview?monthsAhead=6'),
  });

  if (isLoading || !data) return <SkeletonPanel lines={4} />;

  return (
    <div className="space-y-5">
      {/* ── Totals: licences and equipment kept strictly apart ─────────────── */}
      <div className="grid gap-3 sm:grid-cols-2">
        <TotalCard
          icon={<KeyRound size={16} />}
          label="Lisenser – årskostnad"
          value={kr(data.totals.licences.annualCost)}
          sub={`${formatNumber(data.totals.licences.activeCount)} aktive lisenser · per år`}
          tone="accent"
        />
        <TotalCard
          icon={<Package size={16} />}
          label="Utstyr – innkjøpsverdi"
          value={kr(data.totals.equipment.totalCost)}
          sub={`${formatNumber(data.totals.equipment.heldCount)} enheter i bruk · engangskostnad`}
          tone="ink"
        />
      </div>
      <p className="text-ink-soft/70 -mt-2 text-[11px]">
        Lisenser (årlig) og utstyr (engangs) vises alltid hver for seg og summeres aldri sammen.
      </p>

      {/* ── Per company + equipment by type ────────────────────────────────── */}
      <div className="grid gap-5 lg:grid-cols-2">
        <GlassPanel>
          <PanelHeader title="Per selskap" icon={<Cpu size={16} />} />
          <table className="w-full text-sm">
            <thead>
              <tr className="border-hairline text-ink-soft border-b text-left text-xs">
                <th className="pb-2 font-medium">Selskap</th>
                <th className="pb-2 text-right font-medium">Lisenser/år</th>
                <th className="pb-2 text-right font-medium">Utstyr</th>
              </tr>
            </thead>
            <tbody>
              {data.byCompany.map((row) => (
                <tr key={row.company} className="border-hairline/50 border-b last:border-0">
                  <td className="py-2">{COMPANY_LABELS[row.company]}</td>
                  <td className="py-2 text-right tabular-nums">{kr(row.licenceAnnualCost)}</td>
                  <td className="py-2 text-right tabular-nums">{kr(row.equipmentTotalCost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </GlassPanel>

        <GlassPanel>
          <PanelHeader title="Utstyr per type" icon={<Laptop size={16} />} />
          {data.equipmentByCategory.length === 0 ? (
            <EmptyState title="Ingen utstyr registrert ennå." />
          ) : (
            <div className="flex flex-wrap gap-2">
              {data.equipmentByCategory.map((row) => (
                <div
                  key={row.category}
                  className="border-hairline bg-base/40 rounded-lg border px-3 py-2"
                >
                  <p className="text-ink-soft text-[11px]">{EQUIPMENT_CATEGORY_LABELS[row.category]}</p>
                  <p className="text-ink text-sm">
                    <span className="font-display text-base">{row.held}</span> i bruk
                    {row.total !== row.held && (
                      <span className="text-ink-soft/70"> / {row.total} totalt</span>
                    )}
                  </p>
                </div>
              ))}
            </div>
          )}
        </GlassPanel>
      </div>

      {/* ── Renewal calendar ───────────────────────────────────────────────── */}
      <GlassPanel padded={false}>
        <div className="px-5 pt-5">
          <PanelHeader
            title="Fornyelseskalender"
            subtitle="Lisenser som må fornyes eller sies opp de neste seks månedene. Rødt = oppsigelsesfristen er nær."
            icon={<CalendarClock size={16} />}
          />
        </div>
        {data.renewalCalendar.length === 0 ? (
          <div className="pb-4">
            <EmptyState title="Ingenting forfaller de neste seks månedene." />
          </div>
        ) : (
          <div className="overflow-x-auto px-5 pb-5">
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className="border-hairline text-ink-soft border-b text-left text-xs">
                  <th className="py-2 pr-3 font-medium">Lisens</th>
                  <th className="py-2 pr-3 font-medium">Fornyes</th>
                  <th className="py-2 pr-3 font-medium">Si opp innen</th>
                  <th className="py-2 pr-3 text-right font-medium">Årskostnad</th>
                </tr>
              </thead>
              <tbody>
                {data.renewalCalendar.map((row) => (
                  <tr key={row.assignmentId} className="border-hairline/50 border-b last:border-0">
                    <td className="py-2.5 pr-3">
                      <span className="text-ink">
                        {row.productName} – {row.planName}
                      </span>
                      {row.userName && (
                        <span className="text-ink-soft/70 block text-[11px]">{row.userName}</span>
                      )}
                    </td>
                    <td className="text-ink-soft py-2.5 pr-3">
                      {row.renewalDate ? formatDateLong(row.renewalDate) : '—'}
                    </td>
                    <td className="py-2.5 pr-3">
                      {row.cancelBy ? (
                        <span
                          className={cn(
                            'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px]',
                            row.urgent
                              ? 'bg-danger/12 text-danger'
                              : 'bg-raised/60 text-ink-soft',
                          )}
                        >
                          {formatDateLong(row.cancelBy)}
                          <span className="opacity-70">({deadlineHint(row.cancelBy)})</span>
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="text-ink py-2.5 pr-3 text-right tabular-nums">{kr(row.annualCost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </GlassPanel>

      {/* ── "Sist fått" per person and type ────────────────────────────────── */}
      <GlassPanel padded={false}>
        <div className="px-5 pt-5">
          <PanelHeader
            title="Sist fått"
            subtitle="Nyeste mottaksdato per person og type. Returnert utstyr teller også — det er maskinhistorikken."
            icon={<Laptop size={16} />}
          />
        </div>
        {data.lastReceived.length === 0 ? (
          <div className="pb-4">
            <EmptyState title="Ingen mottaksdatoer registrert ennå." />
          </div>
        ) : (
          <div className="overflow-x-auto px-5 pb-5">
            <table className="w-full min-w-[560px] text-sm">
              <thead>
                <tr className="border-hairline text-ink-soft border-b text-left text-xs">
                  <th className="py-2 pr-3 font-medium">Person</th>
                  <th className="py-2 pr-3 font-medium">Type</th>
                  <th className="py-2 pr-3 font-medium">Modell</th>
                  <th className="py-2 pr-3 font-medium">Sist fått</th>
                </tr>
              </thead>
              <tbody>
                {data.lastReceived.map((row) => (
                  <tr
                    key={`${row.userId}-${row.category}`}
                    className="border-hairline/50 border-b last:border-0"
                  >
                    <td className="text-ink py-2 pr-3">{row.userName}</td>
                    <td className="text-ink-soft py-2 pr-3">
                      {EQUIPMENT_CATEGORY_LABELS[row.category]}
                    </td>
                    <td className="text-ink-soft py-2 pr-3">{row.model}</td>
                    <td className="text-ink py-2 pr-3">
                      {formatDateLong(row.receivedAt)}
                      <span className="text-ink-soft/60 ml-1.5 text-[11px]">
                        ({ageHint(row.receivedAt)})
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </GlassPanel>
    </div>
  );
}

function TotalCard({
  icon,
  label,
  value,
  sub,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub: string;
  tone: 'accent' | 'ink';
}) {
  return (
    <GlassPanel className={cn(tone === 'accent' && 'bg-accent/[0.05]')}>
      <div className="flex items-center gap-2">
        <span className={tone === 'accent' ? 'text-accent' : 'text-ink-soft'}>{icon}</span>
        <p className="text-ink-soft text-xs">{label}</p>
      </div>
      <p className="font-display text-ink mt-2 text-3xl tabular-nums">{value}</p>
      <p className="text-ink-soft/70 mt-1 text-[11px]">{sub}</p>
    </GlassPanel>
  );
}

function deadlineHint(date: string): string {
  const days = daysUntil(date);
  if (days < 0) return `${Math.abs(days)} d siden`;
  if (days === 0) return 'i dag';
  return `om ${days} d`;
}

function ageHint(date: string): string {
  const days = -daysUntil(date);
  const years = Math.floor(days / 365);
  const months = Math.floor((days % 365) / 30);
  if (years >= 1) return `${years} år${months >= 1 ? ` ${months} mnd` : ''}`;
  if (months >= 1) return `${months} mnd`;
  return `${days} d`;
}
